/**
 * document-items.ts — Dokumenty: linie dokumentu (increment 1, read-only UI).
 *
 * Współdzielone typy dla nowego API pozycji dokumentu (`document_items` +
 * `document_vat_entries` + 5 nowych kolumn kwot na rdzeniu `documents` —
 * migracja `0094_document_line_items`, silnik obliczeniowy
 * `apps/api/lib/documents/items-calc.ts`). WSPÓLNE dla wszystkich 7 klas
 * dokumentu (nie tylko faktur) — kwoty nagłówkowe (`amount_net`/`amount_vat`/
 * `amount_gross`/`currency_code`/`exchange_rate`) żyją teraz na core `documents`,
 * a nie tylko w `document_invoice_details`.
 *
 * Backend (endpointy GET `/purchase-invoices/:id`, `/sale-invoices/:id`,
 * `/contracts/:id`, `/letters/:id`, `/commercial-docs/:id`, `/internal-docs/:id`)
 * ląduje RÓWNOLEGLE z tym incrementem frontendowym — stąd WSZYSTKIE pola tutaj
 * są opcjonalne na poziomie strony detalu (`DocumentLineItemsFields` /
 * `DocumentHeaderAmountsFields`), a `DocumentItemsSection` toleruje ich brak
 * (empty state). Pola WEWNĄTRZ pojedynczej linii/wiersza VAT mirror'ują
 * nullability kolumn SQL (patrz nagłówek migracji) — tylko `id`/`lineNumber`/
 * `name` (linia) i `vatRateCode` (wiersz VAT) są gwarantowane NOT NULL.
 *
 * Kwoty/ilości/procenty jako STRINGI dziesiętne (konwencja Drizzle `numeric`,
 * zero utraty precyzji przy transporcie JSON) — formatowanie do wyświetlenia
 * robi WYŁĄCZNIE warstwa UI (`DocumentItemsSection`), nigdy tutaj.
 */

/** Pojedyncza linia dokumentu (`document_items`). */
export interface DocumentLineItem {
  id: string;
  lineNumber: number;
  productId: string | null;
  productCode: string | null;
  name: string;
  unit: string | null;
  quantity: string | null;
  unitPriceNet: string | null;
  unitPriceNetInCurrency: string | null;
  discountPercent: string | null;
  discountAmount: string | null;
  discountAmountInCurrency: string | null;
  priceAfterDiscount: string | null;
  priceAfterDiscountInCurrency: string | null;
  valueNet: string | null;
  valueNetInCurrency: string | null;
  vatRateCode: string | null;
  vatRate: string | null;
  valueVat: string | null;
  valueVatInCurrency: string | null;
  valueGross: string | null;
  valueGrossInCurrency: string | null;
  pkwiu: string | null;
  cn: string | null;
  gtin: string | null;
  /** Pola dodatkowe (ext) tej linii, slug → wartość (Fala B2/B3, moduł `document_items`). */
  extValues: Record<string, unknown>;
}

/** Wiersz rozbicia VAT dokumentu per stawka (`document_vat_entries`). */
export interface DocumentVatBreakdownRow {
  vatRateCode: string;
  valueNet: string | null;
  valueVat: string | null;
  valueGross: string | null;
  valueNetInCurrency: string | null;
  valueVatInCurrency: string | null;
  valueGrossInCurrency: string | null;
}

/**
 * Pola linii/rozbicia VAT dołączane do lokalnego typu odpowiedzi GET strony
 * detalu — WSZYSTKIE 7 klas. Opcjonalne (backend ląduje równolegle).
 */
export interface DocumentLineItemsFields {
  items?: DocumentLineItem[];
  vatBreakdown?: DocumentVatBreakdownRow[];
}

/**
 * Pola kwot nagłówkowych dokumentu (migracja 0094, rdzeń `documents`) — dla
 * klas BEZ własnego typowania tych pól (contract/letter/commercial/internal).
 * Faktury (Purchase/Sale) mają `amountNet`/`amountVat`/`amountGross`/
 * `currencyCode`/`exchangeRate` JUŻ typowane (wymagane) we własnym FormData —
 * te dwie strony rozszerzają WYŁĄCZNIE `DocumentLineItemsFields`, nie tym
 * interfejsem (rozszerzenie obydwoma dałoby konflikt required vs optional).
 */
export interface DocumentHeaderAmountsFields {
  amountNet?: string | null;
  amountVat?: string | null;
  amountGross?: string | null;
  currencyCode?: string | null;
  exchangeRate?: string | null;
}

/**
 * Edytowalny wiersz pozycji (stan grida, increment 2). Wszystkie pola jako STRINGI
 * (spójnie z transportem `numeric`; puste = ''). Pola pieniężne (`unitPriceNet`,
 * `discountPercent`) są W WALUCIE DOKUMENTU — bazowy (PLN) odpowiednik i kwoty
 * pochodne (netto/VAT/brutto) liczy SERWER przy zapisie, klient robi tylko podgląd.
 * `key` to stabilny identyfikator klienta (React key + tożsamość wiersza) — NIE
 * jest wysyłany do API.
 */
export interface DocumentLineItemDraft {
  key: string;
  productId: string | null;
  productCode: string;
  name: string;
  unit: string;
  quantity: string;
  unitPriceNet: string;
  discountPercent: string;
  vatRateCode: string;
  vatRate: string;
  pkwiu: string;
  cn: string;
  gtin: string;
  /** Pola dodatkowe (ext) tego wiersza, slug → wartość (Fala B3, moduł `document_items`). */
  extValues: Record<string, unknown>;
}

/**
 * Kształt pozycji WYSYŁANY w tablicy `items` w POST/PATCH dokumentu — mirror
 * `DOCUMENT_ITEM_SCHEMA` z `apps/api/lib/validators/document-items.ts` (nie da się
 * zaimportować spoza apps/api). Kwoty pochodne NIEwysyłane — liczy je serwer.
 */
export interface DocumentItemPayload {
  productId?: string | null;
  productCode?: string | null;
  name: string;
  unit?: string | null;
  quantity: string;
  unitPriceNet: string;
  discount?: { mode: 'percent' | 'amount' | 'priceAfterDiscount'; value: string };
  vatRateCode: string;
  vatRate: string;
  pkwiu?: string | null;
  cn?: string | null;
  gtin?: string | null;
  /** Pola dodatkowe (ext) tej linii, slug → wartość — replace-set, ZAWSZE spread
   * (kontrakt trwałości: brak pola w payloadzie czyściłby zapisane wartości, patrz
   * `draftToPayload`). */
  extValues?: Record<string, unknown>;
}

/**
 * Identyfikatory kolumn tabeli pozycji — jedno źródło prawdy współdzielone przez
 * `DocumentItemsEditor` (edycja, 16 kolumn) i `DocumentItemsSection` (odczyt,
 * podzbiór 10 kolumn — brak `product`/`productCode`/`pkwiu`/`cn`/`gtin`/`actions`, które są
 * albo edytor-only, albo strukturalne dla samej edycji).
 *
 * Kolejność `EDITOR_COLUMN_IDS` to DOMYŚLNA kolejność (gdy layout nie ma jeszcze zapisanego
 * układu kolumn) w OBU trybach: `SECTION_COLUMN_IDS` bierze z niej kolejność, więc odczyt,
 * edycja i edytor układu w kreatorze pokazują kolumny w tej samej kolejności
 * (docs/refactor/EMBEDDED-TABLE-COLUMNS.md, etap 2, decyzja 8). Układ rozwiązuje
 * `resolveEmbeddedTableColumns` z `@saas/shared` na deskryptorach z
 * `lib/embedded-table/document-items-descriptors`.
 */
export type BuiltInDocumentItemColumnId =
  | 'lineNumber'
  | 'product'
  | 'name'
  | 'productCode'
  | 'unit'
  | 'quantity'
  | 'unitPriceNet'
  | 'discountPercent'
  | 'vatRateCode'
  | 'pkwiu'
  | 'cn'
  | 'gtin'
  | 'valueNet'
  | 'valueVat'
  | 'valueGross'
  | 'actions';

/**
 * Token kolumny pola dodatkowego (ext-field) pozycji dokumentu — `ext:<slug>`,
 * gdzie `<slug>` jest slugiem pola z `system_module_fields` modułu `document_items`
 * (patrz `docs/refactor/DOCUMENT-ITEMS-EXT-FIELDS.md` Fala B1/B2). Model kolumn jest
 * OTWARTY: obok 16 wbudowanych może zawierać dowolną liczbę kolumn ext.
 */
export type ExtDocumentItemColumnId = `ext:${string}`;

/** Pełny (otwarty) identyfikator kolumny — wbudowana ALBO pole dodatkowe. */
export type DocumentItemColumnId = BuiltInDocumentItemColumnId | ExtDocumentItemColumnId;

const EXT_COLUMN_PREFIX = 'ext:';

/**
 * Type guard: czy dany identyfikator kolumny wskazuje na pole dodatkowe (`ext:<slug>`). Przyjmuje
 * dowolny napis (np. surowe `id` z układu kolumn), więc służy też do zawężenia go do tokenu `ext:…`.
 */
export function isExtColumnId(id: string): id is ExtDocumentItemColumnId {
  return id.startsWith(EXT_COLUMN_PREFIX);
}

/** Zdejmuje prefiks `ext:` — zwraca sam slug pola dodatkowego. */
export function extColumnSlug(id: ExtDocumentItemColumnId): string {
  return id.slice(EXT_COLUMN_PREFIX.length);
}

/** Buduje pełny token kolumny (`ext:<slug>`) z samego slugu pola dodatkowego. */
export function extColumnId(slug: string): ExtDocumentItemColumnId {
  return `${EXT_COLUMN_PREFIX}${slug}`;
}

/** Pełny zestaw kolumn edytora (16), w dotychczasowej kolejności. */
export const EDITOR_COLUMN_IDS: DocumentItemColumnId[] = [
  'lineNumber',
  'product',
  'name',
  'productCode',
  'unit',
  'quantity',
  'unitPriceNet',
  'discountPercent',
  'vatRateCode',
  'pkwiu',
  'cn',
  'gtin',
  'valueNet',
  'valueVat',
  'valueGross',
  'actions',
];

/**
 * Kolumny, które umie narysować widok odczytu (lista DOZWOLONYCH — kolumna dopisana kiedyś tylko
 * do edycji nie trafi do odczytu sama, bez komórki). Kolejność bierze się z `EDITOR_COLUMN_IDS`.
 */
const READ_VIEW_COLUMN_IDS: ReadonlySet<DocumentItemColumnId> = new Set<DocumentItemColumnId>([
  'lineNumber',
  'name',
  'unit',
  'quantity',
  'unitPriceNet',
  'discountPercent',
  'vatRateCode',
  'valueNet',
  'valueVat',
  'valueGross',
]);

/** Podzbiór obsługiwany przez widok odczytu (10), w kolejności edytora (`EDITOR_COLUMN_IDS`). */
export const SECTION_COLUMN_IDS: DocumentItemColumnId[] = EDITOR_COLUMN_IDS.filter((id) =>
  READ_VIEW_COLUMN_IDS.has(id),
);

/**
 * Klucz i18n nagłówka kolumny — reużywany przez edytor, widok odczytu i panel ustawień
 * bloku. Zawężony do 16 kolumn WBUDOWANYCH — statyczny słownik i18n nie obsłuży
 * `ext:${string}` (etykieta kolumny ext pochodzi z `name` pola w DB, nie z klucza
 * i18n). Wołający z otwartym `DocumentItemColumnId` musi najpierw zawęzić przez
 * `isExtColumnId` (patrz `DocumentItemsEditor`/`DocumentItemsSection`).
 */
export const COLUMN_HEADER_KEYS: Record<BuiltInDocumentItemColumnId, string> = {
  lineNumber: 'tenant.documents.items.columns.lineNumber',
  product: 'tenant.documents.items.columns.product',
  name: 'tenant.documents.items.columns.name',
  productCode: 'tenant.documents.items.columns.productCode',
  unit: 'tenant.documents.items.columns.unit',
  quantity: 'tenant.documents.items.columns.quantity',
  unitPriceNet: 'tenant.documents.items.columns.unitPriceNet',
  discountPercent: 'tenant.documents.items.columns.discountPercent',
  vatRateCode: 'tenant.documents.items.columns.vatRateCode',
  pkwiu: 'tenant.documents.items.columns.pkwiu',
  cn: 'tenant.documents.items.columns.cn',
  gtin: 'tenant.documents.items.columns.gtin',
  valueNet: 'tenant.documents.items.columns.valueNet',
  valueVat: 'tenant.documents.items.columns.valueVat',
  valueGross: 'tenant.documents.items.columns.valueGross',
  actions: 'tenant.documents.items.editor.actions',
};
